import * as React from 'react';
import { cn } from '@noor/lib/cn';

export type MobileCardProps = React.ComponentProps<'section'>;

/**
 * The mobile design's section card: a muted surface with a 20px radius and
 * 16px padding, stacked down the screen.
 *
 * Deliberately NOT the desktop `Card`. On desktop each dashboard widget is a
 * raised white panel on a muted page; the mobile design inverts that — the
 * page is white and each section is a muted inset block. Same content, opposite
 * figure/ground, so it is a separate primitive rather than a `Card` variant.
 */
function MobileCard({ className, ...props }: MobileCardProps) {
  return (
    <section
      data-slot="mobile-card"
      className={cn('flex flex-col gap-4 rounded-[1.25rem] bg-muted p-4', className)}
      {...props}
    />
  );
}

export type MobileCardHeaderProps = React.ComponentProps<'div'>;

/** Header row of a `MobileCard`: title at the leading edge, action trailing. */
function MobileCardHeader({ className, ...props }: MobileCardHeaderProps) {
  return (
    <div
      data-slot="mobile-card-header"
      className={cn('flex items-center gap-4', className)}
      {...props}
    />
  );
}

export type MobileCardTitleProps = React.ComponentProps<'h2'> & {
  /** Muted parenthetical after the title, e.g. the unit "(штук)". */
  hint?: React.ReactNode;
};

/** Section title (Figma Text/14/Medium), with an optional muted unit hint. */
function MobileCardTitle({ className, hint, children, ...props }: MobileCardTitleProps) {
  return (
    <h2
      data-slot="mobile-card-title"
      className={cn(
        'min-w-0 flex-1 text-sm leading-none font-medium tracking-tight text-foreground',
        className,
      )}
      {...props}
    >
      {children}
      {hint !== undefined ? (
        <span className="font-medium text-muted-foreground"> {hint}</span>
      ) : null}
    </h2>
  );
}

MobileCard.Header = MobileCardHeader;
MobileCard.Title = MobileCardTitle;

export { MobileCard, MobileCardHeader, MobileCardTitle };
